import { useCallback } from 'react'

import { XIcon } from 'lucide-react'

import { AddressDisplay } from '~/components/component-library'
import { AddressSearchResult } from '~/types'
import { cn } from '~/utils/tailwind-utils'

type SearchResultProps = {
  result: AddressSearchResult
  highlightText?: string
  isFocused: boolean
  selectResult: (result: AddressSearchResult) => void
  removeResult?: () => void
  onMouseEnter?: () => void
  registerRef: (element: HTMLButtonElement | null) => void
}

export const SearchResult = ({
  registerRef,
  result,
  highlightText,
  isFocused,
  selectResult,
  removeResult,
  onMouseEnter,
}: SearchResultProps) => {
  const { address } = result

  const onSelect = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      selectResult(result)
    },
    [selectResult, result],
  )

  const onRemove = useCallback(
    (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      removeResult?.()
    },
    [removeResult],
  )

  return (
    <button
      key={result.address}
      ref={registerRef}
      onMouseEnter={onMouseEnter}
      onClick={onSelect}
      onMouseDown={(e) => e.preventDefault()}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2.5 py-2.5',
        'hover:bg-gray-100 dark:hover:bg-gray-800',
        isFocused && 'bg-gray-100 dark:bg-gray-800',
      )}
    >
      <AddressDisplay
        address={address}
        layout="standard"
        hidePopover
        clickable={false}
        highlightText={highlightText}
      />
      {!!removeResult && (
        <div
          onClick={onRemove}
          className="cursor-pointer"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onRemove(e)
            }
          }}
        >
          <XIcon className="h-4 w-4 text-secondary" />
        </div>
      )}
    </button>
  )
}

SearchResult.displayName = 'SearchResult'
